import { SurveyAnswer, SurveyBinding, SurveyEvent, SurveyPresentation, SurveyRequest, SurveyResultEvent } from './types';
import { SurveyRecovery, SurveyRecoveryItem, surveyCanonical } from './recovery';
import { canUseGovernedSurveyHelp } from './request';

export type SurveyPhase = 'unresolved' | 'editing' | 'helpComposer' | 'answerClarificationComposer' | 'submitting' | 'recovering' | 'outcomeUnknown' | 'acceptedAwaitingPresentation' | 'readOnly' | 'unavailable';
export interface NativeSurveyState {
  binding?: SurveyBinding;
  phase: SurveyPhase;
  form?: SurveyPresentation;
  draft?: SurveyAnswer;
  draftsByBlock: Record<string, SurveyAnswer | undefined>;
  retainedDraft?: { form: SurveyPresentation; answer: SurveyAnswer };
  helpText: string;
  clarificationText: string;
  pending?: SurveyRequest;
  retryableRequest?: SurveyRequest;
  result?: SurveyResultEvent['payload'];
  presentations: Record<string, SurveyPresentation>;
  history: { form: SurveyPresentation; answer?: SurveyAnswer; helpMode?: 'configured' | 'generative';
    interactionKind?: 'requestHelp' | 'governedSurveyHelp'; helpQuestion?: string; helpReply?: string }[];
  expectedResponses: string[];
  error?: string;
  retryCount: number;
  retryAfter: number;
  disconnected: boolean;
  welcomeText?: string;
  sessionStatus?: 'inProgress' | 'paused' | 'completed' | 'ended';
  recoveryMode?: 'resume' | 'terminal';
  recoveryStatus: 'idle' | 'loading' | 'ready' | 'error';
  recovered: SurveyRecoveryItem[];
  refreshResponseId?: string;
  refreshFailed: boolean;
  refreshAllowed: boolean;
  mayaRestartAfterOnboardingTransition?: string;
}
export const emptySurveyState = (): NativeSurveyState => ({
  phase: 'unresolved', helpText: '', clarificationText: '', presentations: {}, history: [], expectedResponses: [], draftsByBlock: {},
  retryCount: 0, retryAfter: 0, disconnected: false,
  recoveryStatus: 'idle', recovered: [], refreshFailed: false, refreshAllowed: true,
});
export type NativeSurveyAction =
  | { type: 'bind'; binding?: SurveyBinding }
  | { type: 'expect'; responseId: string }
  | { type: 'event'; event: SurveyEvent }
  | { type: 'draft'; answer?: SurveyAnswer }
  | { type: 'help'; open: boolean; text?: string }
  | { type: 'helpText'; text: string }
  | { type: 'clarificationText'; text: string }
  | { type: 'submit'; request: SurveyRequest; now: number }
  | { type: 'unknown'; interactionId: string }
  | { type: 'retry'; now: number }
  | { type: 'invalidate' }
  | { type: 'hydrate'; binding: SurveyBinding; mode: 'resume' | 'terminal'; welcomeText?: string;
      sessionStatus: 'inProgress' | 'paused' | 'completed' | 'ended'; recovery: SurveyRecovery }
  | { type: 'requireRecovery'; mode: 'resume' | 'terminal' }
  | { type: 'recover' }
  | { type: 'recovered'; recovery: SurveyRecovery }
  | { type: 'recoveryFailed' }
  | { type: 'actionUnavailableRecovered'; recovery: SurveyRecovery }
  | { type: 'actionUnavailableRecoveryFailed' }
  | { type: 'submissionRecovered'; recovery: SurveyRecovery }
  | { type: 'refresh'; responseId: string }
  | { type: 'refreshFailed' }
  | { type: 'refreshPolicy'; allowed: boolean }
  | { type: 'refreshFinished' }
  | { type: 'mayaRestarted'; interactionId: string }
  | { type: 'unavailable' };

export const sameSurveyBinding = (left?: SurveyBinding, right?: SurveyBinding) =>
  left?.accountId === right?.accountId && left?.sessionId === right?.sessionId && left?.roomId === right?.roomId;
const sameForm = (form: SurveyPresentation | undefined, formId: string, revision: number) =>
  form?.formId === formId && form.presentationRevision === revision;
const canonical = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonical((value as Record<string, unknown>)[key])}`).join(',')}}`;
  return JSON.stringify(value);
};
const answerBlockKey = (form: SurveyPresentation) => JSON.stringify([
  form.surveyId, form.surveyVersion, form.blockId, form.blockType,
  form.consent?.itemId, form.consent?.version,
]);
const compatibleDraft = (form: SurveyPresentation, answer?: SurveyAnswer): SurveyAnswer | undefined => {
  if (!answer) return undefined;
  if (form.messageType === 'surveyConsent') return answer.type === 'consent' ? answer : undefined;
  if (form.messageType === 'surveyRoute') return answer.type === 'route' &&
    form.options.some((option) => option.id === answer.choiceId) ? answer : undefined;
  if (form.messageType !== 'surveyQuestion') return undefined;
  if (answer.type === 'nonAnswer') return form.nonAnswerOptions.some((option) => option.outcome === answer.outcome) ? answer : undefined;
  if (answer.type !== form.inputType) return undefined;
  if (answer.type === 'singleChoice') {
    const option = form.options.find((item) => item.id === answer.choiceId);
    return option && (option.requiresFreeText || answer.specifyText === undefined) ? answer : undefined;
  }
  if (answer.type === 'multiChoice') return answer.choiceIds.every((choiceId) =>
    form.options.some((option) => option.id === choiceId)) ? answer : undefined;
  return answer.type === 'freeText' && answer.text.length <= (form.maxLength ?? 5000) ? answer : undefined;
};
const rememberedDraft = (state: NativeSurveyState, form: SurveyPresentation) => {
  const key = answerBlockKey(form);
  const answer = Object.prototype.hasOwnProperty.call(state.draftsByBlock, key) ? state.draftsByBlock[key] :
    [...state.history].reverse().find((item) => item.answer && answerBlockKey(item.form) === key)?.answer;
  return compatibleDraft(form, answer);
};
const activate = (state: NativeSurveyState, form: SurveyPresentation): NativeSurveyState => {
  const retained = state.retainedDraft;
  const provisionalAnswer = state.result?.provisionalAnswer;
  const helpReplacement = !!form.notice && !!state.pending &&
    (state.pending.interactiveSurvey.messageType === 'governedSurveyHelp' ||
      state.pending.interactiveSurvey.messageType === 'surveyAction' &&
      state.pending.interactiveSurvey.action === 'requestHelp');
  const draft = provisionalAnswer ?? (retained && answerBlockKey(retained.form) === answerBlockKey(form)
    ? compatibleDraft(form, retained.answer) : rememberedDraft(state, form));
  const answerClarification = provisionalAnswer?.type === 'freeText' && form.messageType === 'surveyQuestion' &&
    form.inputType === 'freeText';
  return {
    ...state,
    history: state.pending && (state.pending.interactiveSurvey.messageType === 'governedSurveyHelp' ||
      state.pending.interactiveSurvey.messageType === 'surveyAction' && state.pending.interactiveSurvey.action === 'requestHelp') && form.notice
      ? state.history.map((item, index) => index === state.history.length - 1 ? { ...item, helpReply: form.notice } : item)
      : state.history,
    form, draft, retainedDraft: undefined, helpText: '', clarificationText: '', pending: undefined, retryableRequest: undefined, result: undefined,
    phase: state.disconnected ? 'unavailable' : answerClarification ? 'answerClarificationComposer' :
      helpReplacement && canUseGovernedSurveyHelp(form) ? 'helpComposer' :
      form.status === 'pending' || form.status === 'paused' ? 'editing' : 'readOnly',
    error: undefined, retryCount: 0, retryAfter: 0, refreshFailed: false,
  };
};
const replacement = (state: NativeSurveyState): NativeSurveyState => {
  const result = state.result;
  if (!result?.replacementFormId || !result.replacementRevision) return state;
  const next = state.presentations[result.replacementFormId];
  if (!sameForm(next, result.replacementFormId, result.replacementRevision)) return state;
  return activate(state, next);
};
const pendingKind = (request: SurveyRequest) => request.interactiveSurvey.messageType === 'surveyAction'
  ? 'surveyAction' : request.interactiveSurvey.messageType;
const recoveryActionable = (item: SurveyRecoveryItem) => !item.pendingInteractionId &&
  (item.presentation.status === 'pending' && item.presentation.footerActions.length > 0 ||
    item.presentation.status === 'paused' && item.presentation.messageType === 'surveyState' &&
      item.presentation.footerActions.length === 1 && item.presentation.footerActions[0].action === 'resume');
const adoptActionRecovery = (
  state: NativeSurveyState,
  recovery: SurveyRecovery,
  keepPending: boolean
): NativeSurveyState => {
  const current = recovery.items[recovery.items.length - 1];
  if (!current?.isCurrent) return { ...state, recoveryStatus: 'error', phase: 'outcomeUnknown' };
  const sameBlock = !!state.form && state.form.surveyId === current.presentation.surveyId &&
    state.form.surveyVersion === current.presentation.surveyVersion && state.form.blockId === current.presentation.blockId;
  return {
    ...state,
    form: current.presentation,
    draft: sameBlock ? state.draft : undefined,
    helpText: '', clarificationText: '',
    pending: keepPending ? state.pending : undefined,
    retryableRequest: undefined,
    result: undefined,
    presentations: Object.fromEntries(recovery.items.map((item) => [item.presentation.formId, item.presentation])),
    history: [],
    recoveryMode: 'resume',
    recoveryStatus: 'ready',
    recovered: recovery.items,
    disconnected: false,
    error: keepPending ? 'action_unavailable' : undefined,
    phase: keepPending ? 'outcomeUnknown' : recoveryActionable(current) ? 'editing' : 'readOnly',
  };
};
export const nativeSurveyReducer = (state: NativeSurveyState, action: NativeSurveyAction): NativeSurveyState => {
  switch (action.type) {
    case 'bind': return sameSurveyBinding(state.binding, action.binding) ? state : { ...emptySurveyState(), binding: action.binding };
    case 'hydrate': {
      if (action.recovery.chatSessionId !== action.binding.sessionId) return state;
      const recovered = action.recovery.items;
      const current = recovered[recovered.length - 1];
      if (!current?.isCurrent) return state;
      const presentations = Object.fromEntries(recovered.map((item) => [item.presentation.formId, item.presentation]));
      const history = recovered.slice(0, -1).map((item) => ({ form: item.presentation, answer: item.result?.acceptedAnswer }));
      const footerActions = current.presentation.footerActions;
      const actionable = action.mode === 'resume' && !current.pendingInteractionId && (
        current.presentation.status === 'pending' && footerActions.length > 0 ||
        current.presentation.status === 'paused' && current.presentation.messageType === 'surveyState' &&
          footerActions.length === 1 && footerActions[0].action === 'resume');
      return {
        ...emptySurveyState(), binding: action.binding, welcomeText: action.welcomeText,
        sessionStatus: action.sessionStatus, recoveryMode: action.mode, recoveryStatus: 'ready',
        recovered, presentations, history, form: current.presentation,
        draft: current.provisionalAnswer,
        phase: actionable ? current.provisionalAnswer ? 'answerClarificationComposer' : 'editing' : 'readOnly',
      };
    }
    case 'expect': return state.binding && !state.form && !state.disconnected ?
      { ...state, expectedResponses: [...state.expectedResponses.slice(-7), action.responseId] } : state;
    case 'mayaRestarted': return state.mayaRestartAfterOnboardingTransition === action.interactionId
      ? { ...state, mayaRestartAfterOnboardingTransition: undefined }
      : state;
    case 'draft': return state.phase === 'editing' ? { ...state, draft: action.answer, error: undefined,
      draftsByBlock: state.form ? { ...state.draftsByBlock, [answerBlockKey(state.form)]: action.answer } : state.draftsByBlock } : state;
    case 'help': return action.open ? state.phase === 'editing' ? { ...state, phase: 'helpComposer', helpText: action.text ?? state.helpText } : state :
      state.phase === 'helpComposer' ? { ...state, phase: 'editing', helpText: '' } : state;
    case 'helpText': return state.phase === 'helpComposer' ? { ...state, helpText: action.text } : state;
    case 'clarificationText': return state.phase === 'answerClarificationComposer'
      ? { ...state, clarificationText: action.text } : state;
    case 'submit': return !state.pending && ['editing', 'helpComposer', 'answerClarificationComposer'].includes(state.phase) ?
      { ...state, pending: action.request, retryableRequest: undefined,
        draft: state.form && action.request.interactiveSurvey.messageType === 'surveyAnswer' &&
          sameForm(state.form, action.request.interactiveSurvey.formId, action.request.interactiveSurvey.presentationRevision)
          ? action.request.interactiveSurvey.answer : state.draft,
        draftsByBlock: state.form && action.request.interactiveSurvey.messageType === 'surveyAnswer' &&
          sameForm(state.form, action.request.interactiveSurvey.formId, action.request.interactiveSurvey.presentationRevision)
          ? { ...state.draftsByBlock, [answerBlockKey(state.form)]: action.request.interactiveSurvey.answer } : state.draftsByBlock,
        helpText: action.request.interactiveSurvey.messageType === 'governedSurveyHelp' ? state.helpText : '',
        phase: 'submitting', retryAfter: action.now + 2000, error: undefined } : state;
    case 'unknown': return state.pending?.interactiveSurvey.interactionId === action.interactionId &&
      ['submitting', 'acceptedAwaitingPresentation'].includes(state.phase) ?
      { ...state, phase: 'outcomeUnknown', error: 'unknown' } : state;
    case 'retry': {
      const request = state.phase === 'outcomeUnknown' ? state.pending : state.phase === 'editing' ? state.retryableRequest : undefined;
      return !state.disconnected && request && state.retryCount < 3 && action.now >= state.retryAfter ?
        { ...state, pending: request, retryableRequest: undefined, phase: 'submitting', retryCount: state.retryCount + 1,
          retryAfter: action.now + Math.min(30000, 2000 * 2 ** (state.retryCount + 1)), error: undefined } : state;
    }
    case 'invalidate': return { ...state, disconnected: true, draft: undefined, draftsByBlock: {}, retainedDraft: undefined, helpText: '', clarificationText: '',
      phase: state.pending ? 'outcomeUnknown' : state.form || state.expectedResponses.length ? 'unavailable' : 'unresolved', error: 'unavailable' };
    case 'requireRecovery': return { ...state, recoveryMode: action.mode,
      refreshAllowed: action.mode === 'resume', phase: 'unresolved' };
    case 'unavailable': return { ...state, phase: state.pending ? 'outcomeUnknown' : 'unavailable', error: 'unavailable' };
    case 'recover': return { ...nativeSurveyReducer(state, { type: 'invalidate' }),
      recoveryStatus: 'loading', refreshResponseId: undefined };
    case 'recoveryFailed': return { ...state, recoveryStatus: 'error', phase: state.pending ? 'outcomeUnknown' : state.phase };
    case 'actionUnavailableRecoveryFailed': return state.phase === 'recovering' && state.pending
      ? { ...state, recoveryStatus: 'error', disconnected: true, phase: 'unavailable' }
      : state;
    case 'actionUnavailableRecovered': {
      const pending = state.pending?.interactiveSurvey;
      if (state.phase !== 'recovering' || !pending || action.recovery.chatSessionId !== state.binding?.sessionId) return state;
      const current = action.recovery.items[action.recovery.items.length - 1];
      const samePending = current?.pendingInteractionId === pending.interactionId &&
        current.pendingKind === pendingKind(state.pending!) &&
        current.presentation.formId === pending.formId &&
        current.presentation.presentationRevision === pending.presentationRevision;
      const accepted = action.recovery.items.some((item) => item.helpExchange?.interactionId === pending.interactionId &&
        item.helpExchange.formId === pending.formId &&
        item.helpExchange.presentationRevision === pending.presentationRevision);
      return adoptActionRecovery(state, action.recovery, samePending && !accepted);
    }
    case 'submissionRecovered': {
      const pending = state.pending?.interactiveSurvey;
      if (!['submitting', 'acceptedAwaitingPresentation'].includes(state.phase) || !state.binding || !pending ||
          action.recovery.chatSessionId !== state.binding.sessionId) return state;
      const receipt = action.recovery.items.find((item) => item.result?.interactionId === pending.interactionId &&
        item.result.formId === pending.formId && item.result.presentationRevision === pending.presentationRevision)?.result;
      const current = action.recovery.items[action.recovery.items.length - 1];
      if (receipt?.status !== 'replayed' || !current?.isCurrent || !receipt.replacementFormId ||
          receipt.replacementRevision === undefined ||
          !sameForm(current.presentation, receipt.replacementFormId, receipt.replacementRevision)) return state;
      const terminal = current.presentation.status === 'completed' || current.presentation.status === 'ended';
      return nativeSurveyReducer(state, { type: 'hydrate', binding: state.binding,
        mode: terminal ? 'terminal' : 'resume',
        sessionStatus: current.presentation.status === 'pending' ? 'inProgress' : current.presentation.status,
        recovery: action.recovery });
    }
    case 'refreshPolicy': return { ...state, refreshAllowed: action.allowed };
    case 'refresh': return state.refreshAllowed && !state.refreshResponseId ?
      { ...state, refreshResponseId: action.responseId, refreshFailed: false } : state;
    case 'refreshFailed': return { ...state, refreshResponseId: undefined, refreshFailed: true, phase: 'unavailable' };
    case 'refreshFinished': return { ...state, refreshResponseId: undefined };
    case 'recovered': {
      if (action.recovery.chatSessionId !== state.binding?.sessionId) return state;
      const old = new Map(state.recovered.map((item) => [item.eventId, item]));
      for (const item of action.recovery.items) {
        const previous = old.get(item.eventId);
        if (previous && (previous.recoverySequence !== item.recoverySequence ||
            surveyCanonical(previous.presentation) !== surveyCanonical(item.presentation) ||
          previous.result && surveyCanonical(previous.result) !== surveyCanonical(item.result) ||
          previous.helpExchange && surveyCanonical(previous.helpExchange) !== surveyCanonical(item.helpExchange))) {
          return { ...state, recoveryStatus: 'error', disconnected: true, phase: state.pending ? 'outcomeUnknown' : 'unavailable' };
        }
        old.delete(item.eventId);
      }
      if (old.size) return { ...state, recoveryStatus: 'error', disconnected: true, phase: state.pending ? 'outcomeUnknown' : 'unavailable' };
      if (!state.disconnected && state.form) {
        return { ...state, recoveryStatus: 'ready', recovered: action.recovery.items };
      }
      let next: NativeSurveyState = { ...state, disconnected: true, recoveryStatus: 'ready', recovered: action.recovery.items };
      const pending = state.pending?.interactiveSurvey;
      const receipt = pending && action.recovery.items.find((item) => item.result?.interactionId === pending.interactionId &&
        item.result.formId === pending.formId && item.result.presentationRevision === pending.presentationRevision)?.result;
      if (receipt) {
        next = nativeSurveyReducer(next, { type: 'event', event: { schemaVersion: 1, target: 'interactive', messageType: 'surveyInteractionResult',
          chatSessionId: action.recovery.chatSessionId, responseId: receipt.interactionId, payload: receipt } });
        if (next.result && ['accepted', 'replayed'].includes(next.result.status) && !next.error) next = { ...next, pending: undefined };
      }
      return { ...next, phase: next.pending ? 'outcomeUnknown' : action.recovery.items.length || next.form ? 'readOnly' : 'unresolved' };
    }
    case 'event': {
      const event = action.event;
      if (!state.binding || event.chatSessionId !== state.binding.sessionId) return state;
      if (event.messageType !== 'surveyInteractionResult') {
        const form = event.payload;
        const existing = state.presentations[form.formId];
        const refreshCorrelated = state.disconnected && state.refreshResponseId === event.responseId;
        if (existing) {
          if (canonical(existing) !== canonical(form)) {
            return { ...state, phase: 'unavailable', disconnected: true, error: 'unavailable' };
          }
          return refreshCorrelated
            ? activate({ ...state, disconnected: false, refreshResponseId: undefined }, form)
            : state;
        }
        if (Object.keys(state.presentations).length >= 100) return { ...state, phase: 'unavailable', disconnected: true, error: 'unavailable' };
        const initial = !state.form && !state.disconnected && state.expectedResponses.includes(event.responseId);
        const pending = state.pending?.interactiveSurvey;
        const pendingHelp = pending && event.responseId === pending.interactionId &&
          (pending.messageType === 'governedSurveyHelp' ||
            pending.messageType === 'surveyAction' && pending.action === 'requestHelp');
        const correlated = pending && (event.responseId === pending.interactionId || state.result?.replacementFormId === form.formId);
        if (!initial && !correlated && !refreshCorrelated) return state.disconnected || !state.form
          ? state : { ...state, phase: 'unavailable', disconnected: true, error: 'unavailable' };
        const next = { ...state, presentations: { ...state.presentations, [form.formId]: form } };
        if (refreshCorrelated) {
          return activate({ ...next, disconnected: false, refreshResponseId: undefined }, form);
        }
        if (pendingHelp && state.form) {
          const retainedDraft = state.retainedDraft ?? (state.draft ? { form: state.form, answer: state.draft } : undefined);
          const interactionKind: 'requestHelp' | 'governedSurveyHelp' =
            pending.messageType === 'governedSurveyHelp' ? 'governedSurveyHelp' : 'requestHelp';
          const history = state.result && ['accepted', 'replayed'].includes(state.result.status) ? state.history :
            [...state.history, { form: state.form, helpMode: state.form.help?.mode, interactionKind,
              helpQuestion: pending.messageType === 'governedSurveyHelp' ? pending.text : undefined }];
          return activate({ ...next, retainedDraft, helpText: '', error: undefined,
            history }, form);
        }
        const pendingSelectorAnswer = !state.result && pending?.messageType === 'surveyAnswer' && state.form &&
          ['languageSelection', 'wordingSelection'].includes(state.form.blockType) &&
          event.responseId === pending.interactionId;
        if (pendingSelectorAnswer) {
          return activate({ ...next,
            history: [...state.history, { form: state.form, answer: pending.answer }] }, form);
        }
        return initial ? activate({ ...next, expectedResponses: [] }, form) : replacement(next);
      }
      const result = event.payload;
      const pending = state.pending?.interactiveSurvey;
      if (!pending || result.interactionId !== pending.interactionId || result.formId !== pending.formId ||
          result.presentationRevision !== pending.presentationRevision || event.responseId !== pending.interactionId) return state;
      if (state.result && ['accepted', 'replayed'].includes(state.result.status)) return state;
      if (result.status === 'retryable') {
        const retryableHelp = pending.messageType === 'governedSurveyHelp' ||
          pending.messageType === 'surveyAction' && pending.action === 'requestHelp';
        if (retryableHelp && result.code === 'action_unavailable') {
          return { ...state, phase: 'recovering', recoveryStatus: 'loading', error: result.code };
        }
        return retryableHelp
          ? { ...state, pending: undefined, retryableRequest: state.pending, phase: 'editing',
              helpText: '', error: result.code ?? 'unknown' }
          : { ...state, phase: 'outcomeUnknown', error: result.code ?? 'unknown' };
      }
      if (result.status === 'rejected') return { ...state, result, phase: 'unavailable', error: result.code ?? 'unavailable' };
      if (result.provisionalAnswer && (pending.messageType !== 'surveyAnswer' ||
          canonical(result.provisionalAnswer) !== canonical(pending.answer))) {
        return { ...state, phase: 'unavailable', error: 'unavailable' };
      }
      if (result.acceptedAnswer && (pending.messageType !== 'surveyAnswer' || canonical(result.acceptedAnswer) !== canonical(pending.answer))) {
        const sameSet = pending.messageType === 'surveyAnswer' && pending.answer.type === 'multiChoice' && result.acceptedAnswer.type === 'multiChoice' &&
          canonical([...pending.answer.choiceIds].sort()) === canonical([...result.acceptedAnswer.choiceIds].sort());
        if (!sameSet) return { ...state, phase: 'unavailable', error: 'unavailable' };
      }
      const endsSurvey = pending.messageType === 'surveyAction' && pending.action === 'skipOnboardingSurvey' &&
        !result.replacementFormId && result.replacementRevision === undefined;
      if (endsSurvey) return { ...emptySurveyState(), binding: state.binding };
      const continuesOnboardingTransition = pending.messageType === 'surveyAction' && pending.action === 'continue' &&
        state.form?.blockType === 'information' && !result.replacementFormId && result.replacementRevision === undefined;
      if (continuesOnboardingTransition) {
        return { ...emptySurveyState(), binding: state.binding,
          mayaRestartAfterOnboardingTransition: result.interactionId };
      }
      const isAnswerClarification = pending.messageType === 'surveyAnswer' && !!result.provisionalAnswer;
      const retainedAnswer = isAnswerClarification
        ? result.provisionalAnswer
        : pending.messageType !== 'surveyAnswer' ? state.draft : undefined;
      const retainedDraft = state.form && retainedAnswer ? { form: state.form, answer: retainedAnswer } : undefined;
      const recoveryComplete = state.recoveryMode === 'resume' && !!result.replacementFormId && result.replacementRevision !== undefined;
      const interactionKind = pending.messageType === 'governedSurveyHelp' ? 'governedSurveyHelp' :
        pending.messageType === 'surveyAction' && pending.action === 'requestHelp' ? 'requestHelp' : undefined;
      const helpMode = interactionKind ? state.form?.help?.mode : undefined;
      return replacement({ ...state, result, phase: 'acceptedAwaitingPresentation', draft: undefined, retainedDraft,
        draftsByBlock: state.form && result.acceptedAnswer
          ? { ...state.draftsByBlock, [answerBlockKey(state.form)]: result.acceptedAnswer } : state.draftsByBlock,
        recoveryMode: recoveryComplete ? undefined : state.recoveryMode,
        recoveryStatus: recoveryComplete ? 'idle' : state.recoveryStatus,
        recovered: recoveryComplete ? [] : state.recovered,
        helpText: '', clarificationText: '', error: undefined, history: state.form && !isAnswerClarification ? [...state.history, { form: state.form,
          answer: result.acceptedAnswer, helpMode, interactionKind,
          helpQuestion: pending.messageType === 'governedSurveyHelp' ? pending.text : undefined }] : state.history });
    }
  }
};